import React, { useLayoutEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tab,
  Tabs,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Engineering as EngineeringIcon,
  Assignment as AssignmentIcon,
  DateRange as DateRangeIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  BarChart as BarChartIcon,
  Group as GroupIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  LocalShipping as TransportIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { setPutPayloadWithParams } from "../services/api";
import LoadingBackDrop from "../scenes/global/LoadingBackDrop";

// Styled components
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  "&:before": {
    display: "none",
  },
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  },
}));
const getPaymentStatus = (request) => {
  if (request.paid === false) {
    return { text: "Unpaid", color: "error", icon: <CloseIcon /> };
  } else if (request.paid === true) {
    return { text: "Paid", color: "success", icon: <CheckCircleIcon /> };
  } else {
    return { text: "Unknown", color: "default", icon: <PendingIcon /> };
  }
};
const COLORS = [
  "#1976d2",
  "#ff9800",
  "#4caf50",
  "#9c27b0",
  "#e91e63",
  "#00bcd4",
  "#795548",
  "#607d8b",
];

const DashboardProgress = ({ loggedUser }) => {
  console.log("logged user " + loggedUser);
  const [currentYear, setCurrentYear] = useState(2026);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  useLayoutEffect(() => {
    loadBasicData(currentYear);
  }, []);

  const loadBasicData = async (year) => {
    setLoading(true);

    await setPutPayloadWithParams
      .setPayloadData("/labReq/all/viewUserProgress/" + loggedUser.id, null, {
        year: year,
      })
      .then(function (response) {
        console.log("data--------------", response.data);
        setData(response.data.pendingRequests);
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setLoading(false);
      });
  };

  const [expanded, setExpanded] = useState(false);
  const [selectedTO, setSelectedTO] = useState(null);
  const [viewType, setViewType] = useState("dashboard"); // 'dashboard' or 'to-requests'
  const [selectedTab, setSelectedTab] = useState(0);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Function to calculate test progress
  const calculateTestProgress = (test) => {
    let steps = 0;
    if (test.finalized) steps++;
    if (test.checked) steps++;
    if (test.checkedByEngineer) steps++;
    return (steps / 3) * 100;
  };

  // Function to get test status
  const getTestStatus = (test) => {
    if (test.finalized && test.checked && test.checkedByEngineer) {
      return { text: "Completed", color: "success", icon: <CheckCircleIcon /> };
    } else if (test.finalized || test.checked) {
      return { text: "In Progress", color: "warning", icon: <PendingIcon /> };
    } else {
      return { text: "Pending", color: "default", icon: <PendingIcon /> };
    }
  };

  // Function to check if a request contains Transport tests
  const isSiteVisit = (request) => {
    const tests = request.labRequestTests || [];
    return tests.some((test) =>
      test.labTest?.testName?.toLowerCase().includes("transport"),
    );
  };

  // Extract lab requests and group by assigned user
  const labRequestsMap = new Map();
  data.forEach((item) => {
    if (item.labRequest && !labRequestsMap.has(item.labRequest.id)) {
      labRequestsMap.set(item.labRequest.id, item.labRequest);
    }
  });

  // Group requests by assigned user (Technical Officer)
  const requestsByUser = new Map();

  Array.from(labRequestsMap.values()).forEach((request) => {
    const assignUser = request.assignUser;
    if (
      assignUser &&
      (assignUser.position?.position === "Technical Officer" ||
        assignUser.position?.position === "Technical Assistant")
    ) {
      if (!requestsByUser.has(assignUser.id)) {
        requestsByUser.set(assignUser.id, {
          user: assignUser,
          requests: [],
        });
      }
      requestsByUser.get(assignUser.id).requests.push(request);
    }
  });

  // Separate regular requests and site visits for each TO
  const requestsByUserWithSiteVisits = new Map();

  Array.from(requestsByUser.entries()).forEach(
    ([userId, { user, requests }]) => {
      const regularRequests = requests.filter((req) => !isSiteVisit(req));
      const siteVisits = requests.filter((req) => isSiteVisit(req));

      requestsByUserWithSiteVisits.set(userId, {
        user,
        regularRequests,
        siteVisits,
        allRequests: requests,
      });
    },
  );

  // Prepare chart data
  const chartData = Array.from(requestsByUser.values()).map(
    ({ user, requests }) => ({
      name: `${user.firstName}`,
      fullName: `${user.firstName} ${user.lastName}`,
      requests: requests.length,
      regularRequests: requests.filter((r) => !isSiteVisit(r)).length,
      siteVisits: requests.filter((r) => isSiteVisit(r)).length,
      completed: requests.filter((r) => r.complete).length,
      pending: requests.filter((r) => !r.complete).length,
      totalValue: requests.reduce((sum, r) => sum + (r.grandTotal || 0), 0),
      userId: user.id,
    }),
  );

  // Get all pending requests
  const pendingRequests = Array.from(labRequestsMap.values()).filter(
    (request) => request.complete === false && request.year == currentYear,
  );

  // Get site visit requests
  const siteVisitRequests = pendingRequests.filter((req) => isSiteVisit(req));
  const regularPendingRequests = pendingRequests.filter(
    (req) => !isSiteVisit(req),
  );

  // Get requests for selected TO
  const getTORequests = (userId) => {
    const userData = requestsByUserWithSiteVisits.get(userId);
    return userData || null;
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Calculate request progress
  const calculateRequestProgress = (request) => {
    const tests = request.labRequestTests || [];
    if (tests.length === 0) return 0;
    const completedTests = tests.filter(
      (test) => test.finalized && test.checked && test.checkedByEngineer,
    ).length;
    return (completedTests / tests.length) * 100;
  };

  // Handle view change
  const handleViewChange = (event, newView) => {
    if (newView) {
      setViewType(newView);
      setSelectedTO(null);
    }
  };

  // Handle TO click
  const handleTOClick = (toData) => {
    setSelectedTO(toData);
    setViewType("to-requests");
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    setViewType("dashboard");
    setSelectedTO(null);
  };

  // Dashboard View
  const renderDashboard = () => (
    <>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        {loading ? <LoadingBackDrop openBackDrop={loading} /> : ""}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "#1a237e",
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <ScienceIcon sx={{ fontSize: 40, color: "#1976d2" }} />
          Lab Quality Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          Manage and track pending laboratory test requests
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="textSecondary"
                      variant="caption"
                      fontWeight={600}
                    >
                      PENDING REQUESTS
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="#1976d2">
                      {regularPendingRequests.length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "#1976d2", width: 56, height: 56 }}>
                    <AssignmentIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="textSecondary"
                      variant="caption"
                      fontWeight={600}
                    >
                      SITE VISITS
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="#e91e63">
                      {siteVisitRequests.length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "#e91e63", width: 56, height: 56 }}>
                    <TransportIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="textSecondary"
                      variant="caption"
                      fontWeight={600}
                    >
                      TOTAL TESTS
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="#ff9800">
                      {data.length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "#ff9800", width: 56, height: 56 }}>
                    <BuildIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="textSecondary"
                      variant="caption"
                      fontWeight={600}
                    >
                      TOTAL VALUE
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="#9c27b0">
                      LKR{" "}
                      {pendingRequests
                        .reduce((sum, req) => sum + (req.grandTotal || 0), 0)
                        .toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "#9c27b0", width: 56, height: 56 }}>
                    <ReceiptIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="textSecondary"
                      variant="caption"
                      fontWeight={600}
                    >
                      UNPAID REQUESTS
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="#d32f2f">
                      {
                        pendingRequests.filter((req) => req.paid === false)
                          .length
                      }
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "#d32f2f", width: 56, height: 56 }}>
                    <CloseIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section - Group by Assigned To */}
        <Typography
          variant="h5"
          fontWeight={600}
          gutterBottom
          sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}
        >
          <BarChartIcon color="primary" />
          Technical Officer Performance Overview
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Bar Chart */}
          <Grid item xs={12} md={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Tests per Technical Officer
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar
                    dataKey="regularRequests"
                    fill="#1976d2"
                    name="Lab Testss"
                  />
                  <Bar dataKey="siteVisits" fill="#e91e63" name="Site Visits" />
                  <Bar dataKey="completed" fill="#4caf50" name="Completed" />
                  <Bar dataKey="pending" fill="#ff9800" name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Pie Chart */}
          {/* <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Request Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      ...chartData.map((item) => ({
                        name: `${item.name} (Regular)`,
                        value: item.regularRequests,
                        fill: "#1976d2",
                      })),
                      ...chartData.map((item) => ({
                        name: `${item.name} (Site Visit)`,
                        value: item.siteVisits,
                        fill: "#e91e63",
                      })),
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {chartData.flatMap((entry, index) => [
                      <Cell key={`regular-${index}`} fill="#1976d2" />,
                      <Cell key={`site-${index}`} fill="#e91e63" />,
                    ])}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid> */}

          {/* Value Chart */}
          {/* <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Total Value by Technical Officer (LKR)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip
                    formatter={(value) => [
                      `LKR ${value.toLocaleString()}`,
                      "Total Value",
                    ]}
                  />
                  <Bar
                    dataKey="totalValue"
                    fill="#9c27b0"
                    name="Total Value (LKR)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid> */}
        </Grid>

        {/* Technical Officers Cards */}
        <Typography
          variant="h5"
          fontWeight={600}
          gutterBottom
          sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}
        >
          <GroupIcon color="secondary" />
          Technical Officers & Their Requests
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Array.from(requestsByUserWithSiteVisits.values()).map(
            ({ user, regularRequests, siteVisits }) => {
              const allRequests = [...regularRequests, ...siteVisits];
              const pendingRegular = regularRequests.filter(
                (r) => !r.complete,
              ).length;
              const pendingSiteVisits = siteVisits.filter(
                (r) => !r.complete,
              ).length;
              const completedCount = allRequests.filter(
                (r) => r.complete,
              ).length;

              return (
                <Grid item xs={12} md={6} lg={4} key={user.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      borderLeft:
                        siteVisits.length > 0 ? "4px solid #e91e63" : "none",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                      },
                    }}
                    onClick={() =>
                      handleTOClick({
                        user,
                        regularRequests,
                        siteVisits,
                        allRequests,
                        pendingCount: pendingRegular + pendingSiteVisits,
                        pendingRegular,
                        pendingSiteVisits,
                        completedCount,
                        unpaidCount: allRequests.filter((r) => r.paid === false)
                          .length, // Add this line
                      })
                    }
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Avatar
                          sx={{ bgcolor: "#1976d2", width: 56, height: 56 }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {user.firstName} {user.lastName}
                            {siteVisits.length > 0 && (
                              <TransportIcon
                                sx={{
                                  ml: 1,
                                  verticalAlign: "middle",
                                  color: "#e91e63",
                                  fontSize: 20,
                                }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {user.position?.position || "Technical Officer"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="textSecondary">
                            Lab Tests
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            color="primary"
                          >
                            {regularRequests.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="textSecondary">
                            Site Visits
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            sx={{ color: "#e91e63" }}
                          >
                            {siteVisits.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="textSecondary">
                            Total
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {allRequests.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="textSecondary">
                            Unpaid
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            color="#d32f2f"
                          >
                            {allRequests.filter((r) => r.paid === false).length}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                        endIcon={<VisibilityIcon />}
                      >
                        View Requests
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            },
          )}
        </Grid>
      </Box>
    </>
  );

  // TO Requests View
  const renderTORequests = () => {
    if (!selectedTO) return null;

    const {
      user,
      regularRequests,
      siteVisits,
      allRequests,
      pendingRegular,
      pendingSiteVisits,
      completedCount,
    } = selectedTO;

    return (
      <>
        <Box
          sx={{
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Button
              variant="outlined"
              onClick={handleBackToDashboard}
              startIcon={<CloseIcon />}
              sx={{ mb: 2 }}
            >
              Back to Dashboard
            </Button>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "#1a237e",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Avatar sx={{ bgcolor: "#1976d2", width: 60, height: 60 }}>
                <PersonIcon sx={{ fontSize: 35 }} />
              </Avatar>
              {user.firstName} {user.lastName}'s Requests
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {user.position?.position} • {user.email} • {user.contactNo}
            </Typography>
          </Box>
          <Box>
            <Chip
              label={`${regularRequests.length} Lab Tests`}
              color="primary"
              sx={{ mr: 1, fontSize: "1rem", p: 2 }}
            />
            <Chip
              label={`${siteVisits.length} Site Visits`}
              sx={{
                mr: 1,
                fontSize: "1rem",
                p: 2,
                bgcolor: "#e91e63",
                color: "white",
              }}
              icon={<TransportIcon />}
            />
            <Chip
              label={`${completedCount} Completed`}
              color="success"
              sx={{ fontSize: "1rem", p: 2, mr: 1 }}
            />
            <Chip
              label={`${allRequests.filter((r) => r.paid === false).length} Unpaid`}
              color="error"
              sx={{ fontSize: "1rem", p: 2 }}
              icon={<CloseIcon />}
            />
          </Box>
        </Box>

        {/* Performance Chart for selected TO */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Request Performance Overview
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  name: "Requests",
                  regular: regularRequests.length,
                  siteVisits: siteVisits.length,
                  pending: allRequests.filter((r) => !r.complete).length,
                  completed: completedCount,
                },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="regular" fill="#1976d2" name="Lab Tests" />
              <Bar dataKey="siteVisits" fill="#e91e63" name="Site Visits" />
              <Bar dataKey="completed" fill="#4caf50" name="Completed" />
              <Bar dataKey="pending" fill="#ff9800" name="Pending" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        {/* Site Visits Section */}
        {siteVisits.length > 0 && (
          <>
            <Typography
              variant="h5"
              fontWeight={600}
              gutterBottom
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#e91e63",
              }}
            >
              <TransportIcon />
              Site Visits
            </Typography>

            {siteVisits.map((request, index) => {
              const requestProgress = calculateRequestProgress(request);
              const tests = request.labRequestTests || [];

              return (
                <StyledAccordion
                  key={`site-${request.id}`}
                  expanded={expanded === `site-${request.id}`}
                  onChange={handleAccordionChange(`site-${request.id}`)}
                  sx={{
                    borderLeft:
                      request.paid === false
                        ? "4px solid #d32f2f"
                        : "4px solid #e91e63",
                    bgcolor: request.paid === false ? "#fff5f5" : "white",
                    "&:hover": {
                      bgcolor: request.paid === false ? "#ffebeb" : "white",
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: "100%" }}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TransportIcon
                              sx={{
                                color:
                                  request.paid === false
                                    ? "#d32f2f"
                                    : "#e91e63",
                              }}
                            />
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              sx={{
                                color:
                                  request.paid === false
                                    ? "#d32f2f"
                                    : "#1a237e",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {/* {request.paid === false && (
                                <Badge color="error" variant="dot">
                                  <CloseIcon sx={{ color: "#d32f2f" }} />
                                </Badge>
                              )} */}
                              {index + 1}). JOB/{request.year}/
                              {request.autoIdByYear} -{" "}
                              {request.project?.substring(0, 60)}...
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <DateRangeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              Reg: {formatDate(request.regDate)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ReceiptIcon fontSize="small" color="action" />
                            <Typography variant="h6">
                              Grand Total : LKR{" "}
                              {request.grandTotal?.toLocaleString()}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <Chip
                            label={`${tests.length} Tests`}
                            size="small"
                            icon={<TransportIcon />}
                            sx={{
                              bgcolor:
                                request.paid === false ? "#d32f2f" : "#e91e63",
                              color: "white",
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          {/* Payment Status Chip */}
                          <Chip
                            icon={
                              request.paid === false ? (
                                <CloseIcon />
                              ) : (
                                <CheckCircleIcon />
                              )
                            }
                            label={request.paid === false ? "UNPAID" : "PAID"}
                            color={request.paid === false ? "error" : "success"}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Box sx={{ width: "100%" }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mb={0.5}
                            >
                              <Typography variant="caption" fontWeight={500}>
                                Progress
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color="primary"
                              >
                                {Math.round(requestProgress)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={requestProgress}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ bgcolor: "#fafbfe", p: 3 }}>
                    {/* Same details as regular requests */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TransportIcon sx={{ color: "#e91e63" }} />
                        Site Visit Details
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: "white" }}
                          >
                            <Typography variant="caption" color="textSecondary">
                              Project
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {request.project}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="textSecondary">
                              Contact Person
                            </Typography>
                            <Typography variant="body2">
                              {request.contactPerson}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="textSecondary">
                              Institute
                            </Typography>
                            <Typography variant="body2">
                              {request.institute?.instituteName}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: "white" }}
                          >
                            <Typography variant="caption" color="textSecondary">
                              Barcode
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace">
                              {request.barcode}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="textSecondary">
                              Total Amount
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={
                                request.paid === false ? "error" : "primary"
                              }
                            >
                              LKR {request.grandTotal?.toLocaleString()}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="textSecondary">
                              Payment Status
                            </Typography>
                            <Chip
                              icon={
                                request.paid === false ? (
                                  <CloseIcon />
                                ) : (
                                  <CheckCircleIcon />
                                )
                              }
                              label={request.paid === false ? "UNPAID" : "PAID"}
                              color={
                                request.paid === false ? "error" : "success"
                              }
                              size="small"
                              sx={{ mt: 0.5, ml: 0.5, fontWeight: 600 }}
                            />
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="textSecondary">
                              Completion Status
                            </Typography>
                            <Chip
                              label={request.complete ? "Complete" : "Pending"}
                              color={request.complete ? "success" : "warning"}
                              size="small"
                              sx={{ mt: 0.5, ml: 0.5 }}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Tests Table */}
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <ScienceIcon color="secondary" />
                      Test Progress
                    </Typography>

                    <TableContainer
                      component={Paper}
                      elevation={0}
                      variant="outlined"
                      sx={{ mt: 2 }}
                    >
                      <Table>
                        <TableHead sx={{ bgcolor: "#fce4ec" }}>
                          <TableRow>
                            <TableCell>
                              <strong>Test Name</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Category</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Quantity</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Status</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Progress</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tests.map((test) => {
                            const progress = calculateTestProgress(test);
                            const status = getTestStatus(test);

                            return (
                              <TableRow key={test.id} hover>
                                <TableCell>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {test.labTest?.testName
                                      ?.toLowerCase()
                                      .includes("transport") && (
                                      <TransportIcon
                                        sx={{ color: "#e91e63", fontSize: 20 }}
                                      />
                                    )}
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                    >
                                      {test.labTest?.testName || "N/A"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      test.labTest?.category?.category ||
                                      "General"
                                    }
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{test.quantity || 0}</TableCell>
                                <TableCell>
                                  <Chip
                                    icon={status.icon}
                                    label={status.text}
                                    color={status.color}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>
                                  <Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={progress}
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        mb: 0.5,
                                      }}
                                      color={
                                        progress === 100 ? "success" : "primary"
                                      }
                                    />
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {Math.round(progress)}% Complete
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </StyledAccordion>
              );
            })}
          </>
        )}

        {/* Regular Requests Section */}
        <Typography
          variant="h5"
          fontWeight={600}
          gutterBottom
          sx={{ mb: 3, mt: siteVisits.length > 0 ? 4 : 0 }}
        >
          Lab Tests
        </Typography>

        {regularRequests.length === 0 ? (
          <Alert severity="info">
            No lab tests assigned to this Technical Officer.
          </Alert>
        ) : (
          regularRequests.map((request, index) => {
            const requestProgress = calculateRequestProgress(request);
            const tests = request.labRequestTests || [];

            return (
              <StyledAccordion
                key={request.id}
                expanded={expanded === `regular-${request.id}`}
                onChange={handleAccordionChange(`regular-${request.id}`)}
                sx={{
                  borderLeft:
                    request.paid === false ? "4px solid #d32f2f" : "none",
                  bgcolor: request.paid === false ? "#fff5f5" : "white",
                  "&:hover": {
                    bgcolor: request.paid === false ? "#ffebeb" : "white",
                  },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: "100%" }}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{
                            color:
                              request.paid === false ? "#d32f2f" : "#1a237e",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {/* {request.paid === false && (
                            <Badge color="error" variant="dot">
                              <CloseIcon sx={{ color: "#d32f2f" }} />
                            </Badge>
                          )} */}
                          {index + 1}). JOB/{request.year}/
                          {request.autoIdByYear} -{" "}
                          {request.project?.substring(0, 60)}...
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DateRangeIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Reg: {formatDate(request.regDate)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ReceiptIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Grand Total : LKR{" "}
                            {request.grandTotal?.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Chip
                          label={`${tests.length} Tests`}
                          size="small"
                          icon={<ScienceIcon />}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        {/* Payment Status Chip - New */}
                        <Chip
                          icon={
                            request.paid === false ? (
                              <CloseIcon />
                            ) : (
                              <CheckCircleIcon />
                            )
                          }
                          label={request.paid === false ? "UNPAID" : "PAID"}
                          color={request.paid === false ? "error" : "success"}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Box sx={{ width: "100%" }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                          >
                            <Typography variant="caption" fontWeight={500}>
                              Progress
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="primary"
                            >
                              {Math.round(requestProgress)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={requestProgress}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ bgcolor: "#fafbfe", p: 3 }}>
                  {/* Request Details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <EngineeringIcon color="primary" />
                      Request Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, bgcolor: "white" }}
                        >
                          <Typography variant="caption" color="textSecondary">
                            Project
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {request.project}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            Contact Person
                          </Typography>
                          <Typography variant="body2">
                            {request.contactPerson}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            Institute
                          </Typography>
                          <Typography variant="body2">
                            {request.institute?.instituteName}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, bgcolor: "white" }}
                        >
                          <Typography variant="caption" color="textSecondary">
                            Barcode
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace">
                            {request.barcode}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            Total Amount
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primary"
                          >
                            LKR {request.grandTotal?.toLocaleString()}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="textSecondary">
                            Completion Status
                          </Typography>
                          <Chip
                            label={request.complete ? "Complete" : "Pending"}
                            color={request.complete ? "success" : "warning"}
                            size="small"
                            sx={{ mt: 0.5, ml: 0.5 }}
                          />
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Tests Table */}
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <ScienceIcon color="secondary" />
                    Test Progress
                  </Typography>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    variant="outlined"
                    sx={{ mt: 2 }}
                  >
                    <Table>
                      <TableHead sx={{ bgcolor: "#e3f2fd" }}>
                        <TableRow>
                          <TableCell>
                            <strong>Test Name</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Category</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Quantity</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Status</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Progress</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tests.map((test) => {
                          const progress = calculateTestProgress(test);
                          const status = getTestStatus(test);

                          return (
                            <TableRow key={test.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {test.labTest?.testName || "N/A"}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  color="warning"
                                >
                                  {test.testDate
                                    ? `Test Date : ${formatDate(test.testDate)}`
                                    : ""}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    test.labTest?.category?.category ||
                                    "General"
                                  }
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{test.quantity || 0}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={status.icon}
                                  label={status.text}
                                  color={status.color}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 150 }}>
                                <Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                                    color={
                                      progress === 100 ? "success" : "primary"
                                    }
                                  />
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {Math.round(progress)}% Complete
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </StyledAccordion>
            );
          })
        )}
      </>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 4, bgcolor: "#f5f7fa", minHeight: "100vh" }}
    >
      {viewType === "dashboard" ? renderDashboard() : renderTORequests()}
    </Container>
  );
};

export default DashboardProgress;
